import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { store } from '../../state/store';
import { useStore } from '../useStore';
import { Icon, type IconName } from '../Icon';
import { ActionMenu } from '../Dropdown';
import { MaterialSwatch } from './MaterialPicker';
import type { Item, Opening, Vec2, Wall } from '../../core/hp/types';
import { contextBarPosition, type SelectionFrame } from '../contextPlacement';
import { chromeRects } from '../chrome';
import { fetchModels, permanentFailure, retryWebBundle, type WebState } from '../../state/catalogApi';
import type { ResizeAxis } from '../../state/itemResize';
import { editProperties } from '../../state/propertyEdit';
import { OPENING_DIMENSION_LETTER, OPENING_DIMENSION_NAME, RESIZE_AXIS_LETTER, RESIZE_AXIS_NAME, releaseResizeHot, setResizeHot, useResizeHot } from '../resizeHot';
import { openingKind } from '../../state/openingProperties';
import type { OpeningDimension } from '../../render/three/Viewer';
import { contextToolbar, sharedItemLimits, type ToolbarEntry } from '../contextToolbar';
import { subscribeModelTags } from '../../state/modelTags';
import { formatNumber, plural, t } from '../../i18n/I18n';

/**
 * The object toolbar: the app's row over the selection, built for each object by `contextToolbar()` in the app's
 * slot order, and under it the app's text buttons (`.context-pills`). One element carries both, so the bar and
 * its second row are placed together and never apart; the bar's handle then moves the bar alone (`Grip`).
 */
export function ContextBar() {
  const state = useStore(({ stats, ...state }) => state);
  const stack = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState<SelectionFrame | null>(null);
  const [position, setPosition] = useState({ left: 12, top: 90 });
  // Where the handle moved the bar, for the selection it was moved on.
  const [moved, setMoved] = useState<{ key: string; offset: Offset } | null>(null);
  // Some commands depend on the model's catalog tags (a glazed door, a flippable window): redraw when they arrive.
  const [, setTagsArrived] = useState(0);
  useEffect(() => subscribeModelTags(() => setTagsArrived(value => value + 1)), []);
  useEffect(() => {
    const update = (event: Event) => setFrame((event as CustomEvent<SelectionFrame | null>).detail);
    window.addEventListener('hp:selection-frame', update);
    return () => window.removeEventListener('hp:selection-frame', update);
  }, []);
  useLayoutEffect(() => {
    const place = () => {
      // Layout size, not the painted rect: the entrance scales the bar, and a rect measured
      // mid-animation would place it against a width it is about to grow out of.
      const element = stack.current; if (!element) return;
      const size = { width: element.offsetWidth, height: element.offsetHeight };
      // In resize mode the item itself is kept clear too: the bar stands beside what is being resized.
      const resizing = state.resizeMode && frame ? [{ x: frame.x, y: frame.y, width: frame.width, height: frame.height }] : [];
      setPosition(contextBarPosition({ anchor: state.selectionAnchor, frame, bar: size, viewport: { width: window.innerWidth, height: window.innerHeight }, keepOut: [...chromeRects(), ...(frame?.keepOut ?? []), ...resizing] }));
    };
    const observer = new ResizeObserver(place);
    if (stack.current) observer.observe(stack.current);
    place(); window.addEventListener('resize', place); return () => { observer.disconnect(); window.removeEventListener('resize', place); };
  }, [state.selection, state.selectionAnchor, state.isCameraDragging, state.resizeMode, frame]);
  const hidden = !state.selection || state.isDragging || state.isCameraDragging || state.showProperties || !!state.materialTarget || !!state.wallRemoval || !!state.roomAction || (state.selection?.kind === 'vertex' && state.mode !== 'plan');
  // As the app's `ContextToolbar.Show` does, a bar shown again — for another object, after a gesture on the plan or the
  // scene hid it, or turned into the resize bar — stands over its object again.
  const key = hidden ? null : JSON.stringify(state.selection) + (state.resizeMode ? ':resize' : '');
  if (moved && moved.key !== key) setMoved(null);
  if (hidden || !state.selection || !key) return null;
  const offset = moved?.offset ?? NO_OFFSET;
  const barStyle = moved ? { translate: `${offset.x}px ${offset.y}px` } : undefined;
  const grip = <Grip offset={offset} onMove={next => setMoved({ key, offset: next })} />;
  // Resize mode keeps the bar where the eye already is, beside the item, and turns it into the
  // mode's own: the sizes and the way out. A new key replays the entrance, so the switch shows.
  if (state.resizeMode && state.selection.kind === 'item') return <div className="context-stack" ref={stack} style={position}>
    <div key="resize" className="context-bar is-resizing" style={barStyle} role="toolbar" aria-label={t('#ContextBarItemSize')}>{grip}<ResizeActions /></div>
  </div>;
  if (state.resizeMode && state.selection.kind === 'opening') return <div className="context-stack" ref={stack} style={position}>
    <div key="resize" className="context-bar is-resizing" style={barStyle} role="toolbar" aria-label={t('#ContextBarOpeningSize')}>{grip}<OpeningResizeActions id={state.selection.id} /></div>
  </div>;
  if (state.selection.kind === 'items') return <div className="context-stack" ref={stack} style={position}>
    <div className="context-bar" style={barStyle} role="toolbar" aria-label={t('#ContextBarSelectionActions')}>{grip}<GroupActions count={state.selection.ids.length} /></div>
  </div>;
  const model = contextToolbar(state, store.level, { planPoint });
  if (!model.entries.length) return null;
  const item = state.selection.kind === 'item' ? store.selectedItem() : undefined;
  const notice = item ? modelStateNotice(state.modelStates[item.modelRef.id]) : null;
  return <div className="context-stack" ref={stack} style={position}>
    <div className="context-bar" style={barStyle} role="toolbar" aria-label={t('#ContextBarSelectionActions')}>
      {grip}
      {item && notice && <button type="button" className={'cb-model-state' + (notice.pending ? ' pending' : '')} title={notice.detail} aria-label={`${notice.text}. ${notice.detail}`} disabled={!notice.retry && !notice.pending} aria-disabled={!notice.retry} onClick={() => { if (notice.retry) void retryWebBundle(item.modelRef.id); }}><Icon name={notice.pending ? 'recent' : 'info'} size={16} /><span>{notice.text}</span></button>}
      {model.entries.map(entry => <Entry key={entry.slot} entry={entry} />)}
    </div>
    {model.pills.length > 0 && <div className="context-pills" role="toolbar" aria-label={t('#ContextBarSelectionShortcuts')}>
      {model.pills.map(pill => <button key={pill.id} type="button" className={'cb-pill glass' + (pill.locked ? ' is-locked' : '')} onClick={pill.run}>
        {pill.locked && <Icon name="padlock" size={16} />}{pill.label}</button>)}
    </div>}
  </div>;
}

type Offset = { x: number; y: number };
const NO_OFFSET: Offset = { x: 0, y: 0 };
/** The bar keeps this far inside the stage when dragged. */
const GRIP_MARGIN = 8;
/**
 * The bar's handle, the app's `ContextToolbarDragHandleButton` (Figma «Button states», «Movement»): the move cursor
 * over it, a grabbing hand while it is held, and a drag that takes the bar alone — the text buttons under the object
 * stay — anywhere the bar stays whole on the stage. A click does nothing; the buttons are reached by Tab without it.
 */
function Grip({ offset, onMove }: { offset: Offset; onMove: (offset: Offset) => void }) {
  const drag = useRef<{ pointer: number; x: number; y: number; from: Offset; room: { left: number; right: number; top: number; bottom: number } } | null>(null);
  const [held, setHeld] = useState(false);
  const release = (event: ReactPointerEvent) => {
    if (drag.current?.pointer !== event.pointerId) return;
    drag.current = null; setHeld(false);
  };
  return <span className="cb-grip" data-held={held || undefined} title={t('#ContextBarDragToMove')} aria-hidden="true"
    onPointerDown={event => {
      if (event.button !== 0) return;
      event.preventDefault(); event.stopPropagation();
      const bar = event.currentTarget.closest('.context-bar'); if (!bar) return;
      const rect = bar.getBoundingClientRect();
      const stage = bar.closest('.stage')?.getBoundingClientRect() ?? { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };
      // The window's header floats over the stage's top and would take the bar's presses there.
      const top = Math.max(stage.top, bar.closest('.editor')?.querySelector(':scope > .topbar')?.getBoundingClientRect().bottom ?? stage.top);
      // How far each way the bar may go from here and stay whole on the stage; a bar already past an edge is not pulled back.
      const room = { left: Math.min(0, stage.left + GRIP_MARGIN - rect.left), right: Math.max(0, stage.right - GRIP_MARGIN - rect.right),
        top: Math.min(0, top + GRIP_MARGIN - rect.top), bottom: Math.max(0, stage.bottom - GRIP_MARGIN - rect.bottom) };
      drag.current = { pointer: event.pointerId, x: event.clientX, y: event.clientY, from: offset, room };
      event.currentTarget.setPointerCapture(event.pointerId);
      setHeld(true);
    }}
    onPointerMove={event => {
      const start = drag.current; if (!start || start.pointer !== event.pointerId) return;
      const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));
      onMove({ x: Math.round(start.from.x + clamp(event.clientX - start.x, start.room.left, start.room.right)),
        y: Math.round(start.from.y + clamp(event.clientY - start.y, start.room.top, start.room.bottom)) });
    }}
    onPointerUp={release} onPointerCancel={release} onLostPointerCapture={release}>
    <Icon name="grip" size={16} />
  </span>;
}

/** The resize bar: the item's three sizes, typed in place in centimetres and each lit together with
 * its handle and face, and «Готово» (Esc and a tap beside the item end the mode too, and keep the
 * item selected). */
function ResizeActions() {
  const item = store.selectedItem(), hot = useResizeHot();
  const original = useCatalogSize(item?.modelRef.kind === 'none' ? undefined : item?.modelRef.id);
  if (!item) return null;
  // The app's ResetSizeFurnWithFloatingButton: while the piece's size is not its catalog's, it goes back to it.
  const resized = !!original && (['w', 'h', 'd'] as const).some(axis => Math.abs(item.size[axis] - original[axis]) > .005);
  const reset = () => {
    const level = store.level;
    if (!level || !original) return;
    // What hangs from the ceiling keeps its top there, anything else its bottom, as the inspector's sizes do.
    const elevation = item.placement === 'ceiling' ? Math.max(0, item.elevation + item.size.h - original.h) : item.elevation;
    const result = editProperties(level, { kind: 'item', id: item.id, values: { size: { ...original }, position: { ...item.position }, elevation, rotation: item.rotation } });
    if (!result.level) { if (result.error) store.set({ error: result.error }); return; }
    store.clearPropertiesPreview();
    store.commitLevel('', () => result.level!);
  };
  return <>
    <div className="cb-sizes" role="group" aria-label={t('#ContextBarSizesCm')}>
      {(['w', 'd', 'h'] as const).map(axis => <SizeField key={axis} axis={axis} item={item} hot={hot === axis} />)}
      <span className="cb-size-unit" aria-hidden="true">{t('cm')}</span>
    </div>
    {resized && <button type="button" className="cb-reset" title={t('#ContextBarResetToCatalogSize')} onClick={reset}><Icon name="undo" size={16} />{t('#PropsReset')}</button>}
    <button type="button" className="cb-done" title={t('#ContextBarDoneEsc')} onClick={() => store.setResizeMode(false)}><Icon name="check" size={17} />{t('Done')}</button>
  </>;
}

/** An opening's resize bar: its width, its height and a window's sill, typed in centimetres, and «Готово». */
function OpeningResizeActions({ id }: { id: string }) {
  const level = store.level, opening = level?.openings.find(value => value.id === id), wall = level?.walls.find(value => value.id === opening?.wallId);
  if (!opening || !wall) return null;
  return <>
    <div className="cb-sizes" role="group" aria-label={t('#ContextBarOpeningSizesCm')}>
      <OpeningSizeField kind="width" opening={opening} wall={wall} />
      <OpeningSizeField kind="height" opening={opening} wall={wall} />
      {openingKind(opening) === 'window' && <OpeningSizeField kind="sill" opening={opening} wall={wall} />}
      <span className="cb-size-unit" aria-hidden="true">{t('cm')}</span>
    </div>
    <button type="button" className="cb-done" title={t('#ContextBarDoneEsc')} onClick={() => store.setResizeMode(false)}><Icon name="check" size={17} />{t('Done')}</button>
  </>;
}

/**
 * One of an opening's sizes in the resize bar, in whole centimetres: a width keeps the opening's middle, a height its
 * sill, a sill its height; up to what the wall leaves it (the inspector's limits). The arrows step it by 1 cm, with
 * Shift by 10, a run of steps one undo step; a size the wall refuses says why.
 */
function OpeningSizeField({ kind, opening, wall }: { kind: OpeningDimension; opening: Opening; wall: Wall }) {
  const size = Math.round(opening[kind === 'width' ? 'width' : kind === 'height' ? 'height' : 'sill'] * 100);
  const limit = kind === 'width' ? { min: 1, max: Math.floor((wall.length - .04) * 100) }
    : kind === 'height' ? { min: 1, max: Math.floor((wall.height - opening.sill) * 100) } : { min: 0, max: Math.floor((wall.height - opening.height) * 100) };
  const [draft, setDraft] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);
  const apply = (value: number, run = false) => {
    const next = Math.round(value), level = store.level;
    if (next === size) return true;
    if (!level) return false;
    const values = kind === 'width' ? { width: next / 100 } : kind === 'height' ? { height: next / 100 } : { sill: next / 100 };
    const result = editProperties(level, { kind: 'opening', id: opening.id, values });
    if (!result.level) { if (result.error) store.set({ error: result.error }); return false; }
    store.clearPropertiesPreview();
    store.commitLevel(run ? `opening-size:${opening.id}:${kind}` : '', () => result.level!);
    return true;
  };
  const commit = () => {
    if (draft === null) return true;
    const value = Number(draft.replace(',', '.'));
    if (!Number.isFinite(value) || value < limit.min || value > limit.max) { setInvalid(true); return false; }
    setDraft(null); setInvalid(false);
    return apply(value);
  };
  const text = draft ?? String(size), name = OPENING_DIMENSION_NAME[kind];
  return <label className="cb-size" data-opening={kind} data-invalid={invalid || undefined} title={invalid ? t('#ContextBarFieldRange', name, limit.min, formatNumber(limit.max)) : t('#ContextBarFieldCm', name)}>
    <b aria-hidden="true">{OPENING_DIMENSION_LETTER[kind]}</b>
    <input className="cb-size-input" data-opening={kind} inputMode="decimal" enterKeyHint="done" autoComplete="off" spellCheck={false}
      aria-label={t('#ContextBarFieldCm', name)} aria-invalid={invalid || undefined} value={text} style={{ width: `${Math.max(2, text.length)}ch` }}
      onFocus={event => event.currentTarget.select()}
      onBlur={() => { if (!commit()) { setDraft(null); setInvalid(false); } }}
      onChange={event => { setDraft(event.target.value.replace(/[^\d.,]/g, '').slice(0, 7)); setInvalid(false); }}
      onKeyDown={event => {
        if (event.key === 'Enter') { event.preventDefault(); if (commit()) event.currentTarget.select(); }
        else if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setDraft(null); setInvalid(false); event.currentTarget.blur(); }
        else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          event.preventDefault();
          const base = draft === null ? size : Number(draft.replace(',', '.'));
          const next = Math.min(limit.max, Math.max(limit.min, (Number.isFinite(base) ? base : size) + (event.key === 'ArrowUp' ? 1 : -1) * (event.shiftKey ? 10 : 1)));
          setDraft(null); setInvalid(false); apply(next, true);
          const field = event.currentTarget; requestAnimationFrame(() => { if (document.activeElement === field) field.select(); });
        }
      }} />
    {invalid && <span className="cb-size-error" role="alert">{t('#ContextBarRange', limit.min, formatNumber(limit.max))}</span>}
  </label>;
}

/** A model's size in its catalog, metres, once its card has come (cached per model). */
const catalogSizes = new Map<string, Promise<Item['size'] | null>>();
function useCatalogSize(modelId: string | undefined): Item['size'] | null {
  const [size, setSize] = useState<{ id: string; value: Item['size'] | null } | null>(null);
  useEffect(() => {
    if (!modelId) return;
    let live = true;
    let request = catalogSizes.get(modelId);
    if (!request) {
      // The catalog keys an app's own model by its path even when asked by its id, as modelVariations finds it.
      request = fetchModels([modelId]).then(found => {
        const model = found[modelId] ?? Object.values(found).find(value => value.id === modelId || value.builtinId === modelId);
        const size = model && [model.width, model.height, model.depth].map(Number);
        return size?.every(value => Number.isFinite(value) && value > 0) ? { w: size[0] / 100, h: size[1] / 100, d: size[2] / 100 } : null;
      }, () => { catalogSizes.delete(modelId); return null; });
      catalogSizes.set(modelId, request);
    }
    void request.then(value => { if (live) setSize({ id: modelId, value }); });
    return () => { live = false; };
  }, [modelId]);
  return size && size.id === modelId ? size.value : null;
}
/** Sizes typed in the bar, as the inspector sets them: the item keeps its centre, and its bottom for height. */
const SIZE_LIMIT = { min: 1, max: 10000 };
/** One size of the resize bar: Enter or leaving the field applies it (one undo step), Escape takes the
 * typing back, ↑ ↓ step it by 1 cm (10 with Shift) at once, a run of steps being one undo step. A
 * number out of range says so and stays until it is changed or taken back. */
function SizeField({ axis, item, hot }: { axis: ResizeAxis; item: Item; hot: boolean }) {
  const size = Math.round(item.size[axis] * 100);
  const [draft, setDraft] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);
  const apply = (value: number, run = false) => {
    const next = Math.round(value), level = store.level;
    if (next === size) return true;
    if (!level) return false;
    const result = editProperties(level, { kind: 'item', id: item.id, values: { size: { ...item.size, [axis]: next / 100 }, position: { ...item.position }, elevation: item.elevation, rotation: item.rotation } });
    if (!result.level) { if (result.error) store.set({ error: result.error }); return false; }
    store.clearPropertiesPreview();
    store.commitLevel(run ? `resize-field:${item.id}:${axis}` : '', () => result.level!);
    return true;
  };
  const commit = () => {
    if (draft === null) return true;
    const value = Number(draft.replace(',', '.'));
    if (!Number.isFinite(value) || value < SIZE_LIMIT.min || value > SIZE_LIMIT.max) { setInvalid(true); return false; }
    setDraft(null); setInvalid(false);
    return apply(value);
  };
  const text = draft ?? String(size);
  return <label className="cb-size" data-axis={axis} data-hot={hot || undefined} data-invalid={invalid || undefined}
    title={invalid ? t('#ContextBarFieldRange', RESIZE_AXIS_NAME[axis], SIZE_LIMIT.min, formatNumber(SIZE_LIMIT.max)) : t('#ContextBarFieldCm', RESIZE_AXIS_NAME[axis])}
    onPointerEnter={event => { if (event.pointerType === 'mouse') setResizeHot(axis); }}
    onPointerLeave={event => { if (event.pointerType === 'mouse' && !event.currentTarget.contains(document.activeElement)) releaseResizeHot(axis); }}>
    <b aria-hidden="true">{RESIZE_AXIS_LETTER[axis]}</b>
    <input className="cb-size-input" data-axis={axis} inputMode="decimal" enterKeyHint="done" autoComplete="off" spellCheck={false}
      aria-label={t('#ContextBarFieldCm', RESIZE_AXIS_NAME[axis])} aria-invalid={invalid || undefined} value={text} style={{ width: `${Math.max(2, text.length)}ch` }}
      onFocus={event => { setResizeHot(axis); event.currentTarget.select(); }}
      onBlur={() => { if (!commit()) { setDraft(null); setInvalid(false); } releaseResizeHot(axis); }}
      onChange={event => { setDraft(event.target.value.replace(/[^\d.,]/g, '').slice(0, 7)); setInvalid(false); }}
      onKeyDown={event => {
        if (event.key === 'Enter') { event.preventDefault(); if (commit()) event.currentTarget.select(); }
        else if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setDraft(null); setInvalid(false); event.currentTarget.blur(); }
        else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          event.preventDefault();
          const base = draft === null ? size : Number(draft.replace(',', '.'));
          const next = Math.min(SIZE_LIMIT.max, Math.max(SIZE_LIMIT.min, (Number.isFinite(base) ? base : size) + (event.key === 'ArrowUp' ? 1 : -1) * (event.shiftKey ? 10 : 1)));
          setDraft(null); setInvalid(false); apply(next, true);
          // The new number stays selected, as after Enter: the next digits replace it.
          const field = event.currentTarget; requestAnimationFrame(() => { if (document.activeElement === field) field.select(); });
        }
      }} />
    {invalid && <span className="cb-size-error" role="alert">{t('#ContextBarRange', SIZE_LIMIT.min, formatNumber(SIZE_LIMIT.max))}</span>}
  </label>;
}

/** Where the selection was picked, on the plan: «Разделить стену» puts its corner there, as the app puts it at the last touch. */
function planPoint(): Vec2 | null {
  const anchor = store.get().selectionAnchor, canvas = document.querySelector<HTMLCanvasElement>('canvas.plan-canvas');
  const view = (canvas as unknown as { __planView?: { toPlan(x: number, y: number): Vec2 } } | null)?.__planView;
  if (!anchor || !canvas || !view) return null;
  const rect = canvas.getBoundingClientRect();
  return view.toPlan(anchor.x - rect.left, anchor.y - rect.top);
}

function Entry({ entry }: { entry: ToolbarEntry }) {
  if (entry.disabled && !entry.swatch) return <Action icon={entry.icon ?? 'more'} label={entry.label} danger={entry.danger} off={entry.disabled} onClick={() => {}} />;
  if (entry.menu) return <ActionMenu label={entry.label} trigger={<Icon name={entry.icon ?? 'more'} size={20} />} triggerClassName="cb-btn"
    heading={entry.heading && <>{entry.heading.title}<small>{entry.heading.detail}</small></>}
    items={entry.menu.map(row => ({ id: row.id, label: row.label, icon: <Icon name={row.icon} />, onSelect: row.run }))} />;
  if (entry.swatch) return <button type="button" className="cb-btn cb-swatch" title={entry.disabled ? `${entry.label}. ${entry.disabled}` : entry.label} aria-label={entry.label}
    aria-description={entry.disabled} aria-disabled={entry.disabled ? true : undefined} onClick={entry.disabled ? () => store.set({ error: entry.disabled }) : entry.run}><MaterialSwatch material={entry.swatch.material} previewUrl={entry.swatch.previewUrl} /></button>;
  return <Action icon={entry.icon ?? 'more'} label={entry.label} danger={entry.danger} active={entry.pressed} onClick={() => entry.run?.()} />;
}

/** `off`: shown but off, the reason in its tooltip and said again on a press, where a finger has no tooltip. */
function Action({ icon, label, onClick, danger, active, labeled, off }: { icon: IconName; label: string; onClick: () => void; danger?: boolean; active?: boolean; labeled?: boolean; off?: string }) {
  return <button type="button" className={'cb-btn' + (danger ? ' danger' : '')} title={off ? `${label}. ${off}` : label} aria-label={label} aria-description={off} aria-disabled={off ? true : undefined}
    aria-pressed={off ? undefined : active} onClick={off ? () => store.set({ error: off }) : onClick}><Icon name={icon} size={20} />{labeled && <span>{label}</span>}</button>;
}
/** What the chip says about a model the scene shows as a placeholder, and whether a tap can help. */
export function modelStateNotice(state: WebState | undefined): { text: string; detail: string; pending: boolean; retry: boolean } | null {
  if (!state) return null;
  switch (state.status) {
    case 'queued': return { text: t('#ContextBarModelPreparing'), detail: t('#ContextBarModelQueued'), pending: true, retry: false };
    case 'running': return { text: t('#ContextBarModelPreparing'), detail: t('#ContextBarModelConverting'), pending: true, retry: false };
    // The queue's `error` is English for the log; a permanent failure answers the same to every try, so none is offered.
    case 'error': return permanentFailure(state.code)
      ? { text: t('#ContextBarModelFailed'), detail: t('#ContextBarModelDamaged'), pending: false, retry: false }
      : { text: t('#ContextBarModelFailed'), detail: t('#ContextBarModelFailedRetry'), pending: false, retry: true };
    case 'missing': return { text: t('#ContextBarModelMissing'), detail: t('#ContextBarModelMissingDetail'), pending: false, retry: false };
    case 'unavailable': return { text: t('#ContextBarCatalogUnavailable'), detail: t('#ContextBarCatalogUnavailableDetail', state.error ?? t('#ContextBarNoConnection')), pending: false, retry: true };
    default: return null;
  }
}

/** A Shift-gathered group: what can be done to all of it at once. */
function GroupActions({ count }: { count: number }) {
  return <>
    <span className="cb-title" aria-live="polite">{plural('#ContextBarItemCount', count)}</span>
    <Action icon="copy" label={t('#ContextBarDuplicateSelected')} onClick={() => { const anchor = store.get().selectionAnchor; store.duplicateSelection(); store.set({ selectionAnchor: anchor }); }} />
    <Action icon="trash" label={t('#ContextBarDeleteSelected')} danger onClick={() => store.deleteSelection()} />
  </>;
}

/** The inspector's own row of item actions (`PropertiesSheet`, «Действия с предметом»), with their captions. */
export function ItemActions({ onReplace }: { embedded?: boolean; onReplace?: () => void } = {}) {
  const item = store.selectedItem(); if (!item) return null;
  const state = store.get();
  const notice = modelStateNotice(state.modelStates[item.modelRef.id]);
  const mirror = () => store.commitLevel('', level => ({ ...level, items: level.items.map(target => target.id === item.id ? { ...target, mirrored: !target.mirrored, scale: [-target.scale[0], target.scale[1], target.scale[2]] } : target) }));
  const { props, turn } = sharedItemLimits(state.edit, item), locked = item.locked ? t('#WebItemLocked') : undefined;
  return <>
    {notice && <button type="button" className={'cb-model-state' + (notice.pending ? ' pending' : '')} title={notice.detail} aria-label={`${notice.text}. ${notice.detail}`} disabled={!notice.retry && !notice.pending} aria-disabled={!notice.retry} onClick={() => { if (notice.retry) void retryWebBundle(item.modelRef.id); }}><Icon name={notice.pending ? 'recent' : 'info'} size={16} /><span>{notice.text}</span></button>}
    <Action labeled icon="ruler" label={t('Change Size')} active={state.resizeMode} off={locked ?? props} onClick={() => store.setResizeMode(!state.resizeMode)} />
    <Action labeled icon="copy" label={t('#ToolbarDuplicateItem')} onClick={() => { const anchor = store.get().selectionAnchor; store.duplicateSelection(); store.set({ selectionAnchor: anchor }); }} />
    <Action labeled icon="rotate" label={t('#ContextBarRotate90')} off={locked ?? turn} onClick={() => { store.rotateItem(item.id, item.rotation + Math.PI / 2); store.endStroke(); }} />
    <Action labeled icon="mirror" label={t('#ContextBarMirror')} off={props} onClick={mirror} />
    <Action labeled icon="trash" label={t('#DeleteItem')} danger off={locked} onClick={() => store.deleteSelection()} />
    <Action labeled icon="rotate" label={t('#ContextBarResetRotation')} off={locked ?? turn} onClick={() => { store.rotateItem(item.id, 0); store.endStroke(); }} />
    {onReplace && <Action labeled icon="sliders" label={t('#ToolbarReplaceItem')} off={props} onClick={onReplace} />}
  </>;
}
